import type {
  ComponentType,
  Context,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { createContext, useContext } from 'react';

import type { Host } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';

export interface ContextHelper {
  consumeContext: Host['consumeContext'];
  provideContext: (
    runtime: Runtime,
    xingletName: string
  ) => ReturnType<Host['provideContext']>;
  ContextProvider: ComponentType<
    PropsWithChildren<{ name: string; runtime: Runtime }>
  >;
}

type ContextContainer = { context: Context<unknown>; value: unknown };

function getContexts(runtime: Runtime, xingletName: string): string[] {
  return runtime.metadataMap[xingletName]?.contributes?.contexts ?? [];
}

export function createContextHelper(): ContextHelper {
  const publishedContexts = new Map<string, ContextContainer>();

  return {
    /**
     * This function is used to store provided contexts and their value
     * under a given context-id.
     *
     * It should throw if the given context-id is not announced in the
     * xinglets metadata.
     */
    provideContext:
      (runtime, xingletName) => (contextId: string, contextValue: unknown) => {
        const isKnownContext = getContexts(runtime, xingletName).some(
          (declaredContext) => declaredContext === contextId
        );

        if (!isKnownContext) {
          throw new Error(
            `Unknown context '${contextId}'. Missed to add to your xinglet.json?`
          );
        }

        const context = createContext(contextValue);
        context.displayName = contextId;

        publishedContexts.set(contextId, { context, value: contextValue });
      },

    /**
     * This function will take a context-id and return the stored context
     * value for that. It must be used as a react custom hook.
     */
    consumeContext<T>(contextId: string): T {
      const container = publishedContexts.get(contextId);
      if (!container) {
        throw new Error(`Unknown context '${contextId}'`);
      }
      // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/consistent-type-assertions
      return useContext(container.context) as T;
    },

    /**
     * This function is a react component wrapping it's children in
     * all known published contexts for for the given xinglet.
     */
    ContextProvider: ({
      name: xingletName,
      runtime,
      children,
    }: {
      name: string;
      runtime: Runtime;
      children?: ReactNode;
    }) => {
      const contexts = getContexts(runtime, xingletName)
        .map((contextId) => publishedContexts.get(contextId))
        .filter((container) => container !== undefined);

      return contexts.reduce(
        (children, container) => {
          return (
            <container.context.Provider value={container.value}>
              {children}
            </container.context.Provider>
          );
        },
        <>{children}</>
      );
    },
  };
}
